"use client";
import React, { useState } from "react";

import dynamic from "next/dynamic";
import { useParams, usePathname, useSearchParams } from "next/navigation";

import { GlobalPathParams } from "~/app/[locale]/types";
import { SessionExpiredPopUpProps } from "~/bff/components/SessionExpiredPopUp";
import { Params } from "~/constants/request";
import { replaceHistoryState } from "~/helpers/history";
import { importComponentByName } from "~/helpers/import-component-by-name";
import { useLoginLogout } from "~/hooks/use-login-logout";
import { AzureConfigurator } from "~/services/azure-configurator/azure-configurator";

import type { ConfirmationPopUpProps } from "../pop-up/components/confirmation-pop-up/types";

const ConfirmationPopUp = dynamic<ConfirmationPopUpProps>(async () =>
  importComponentByName(
    import("~/components/pop-up/components/confirmation-pop-up/component"),
    "ConfirmationPopUp",
  ),
);

export const SessionExpiredPopUp: React.FC<SessionExpiredPopUpProps> = ({
  continueAsAGuestButtonText,
  description,
  signInButtonText,
  title,
}): React.ReactElement => {
  const { locale } = useParams<GlobalPathParams>();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const { startLogin } = useLoginLogout(locale);

  const [isSessionExpiredPopupOpen, setIsSessionExpiredPopupOpen] = useState(false);

  const handleSessionExpiredPopupClose = React.useCallback(() => {
    setIsSessionExpiredPopupOpen(false);
  }, []);

  const handleSessionExpiredPopupConfirm = React.useCallback(() => {
    setIsSessionExpiredPopupOpen(false);
    startLogin(pathname);
  }, [pathname, startLogin]);

  const sessionExpiredPopupProps: ConfirmationPopUpProps = {
    title,
    description,
    cancelButtonName: continueAsAGuestButtonText ?? "",
    deleteButtonText: signInButtonText,
    open: isSessionExpiredPopupOpen,
    handleClose: handleSessionExpiredPopupClose,
    handleConfirm: handleSessionExpiredPopupConfirm,
    disableCloseButton: true,
  };

  React.useEffect(() => {
    const sessionExpiredPopup = new URLSearchParams(searchParams).get(
      Params.SESSION_EXPIRED_POPUP,
    );
    const isMyAccountEnabled =
      AzureConfigurator.getConfig(locale)?.featureFlagList?.myAccount?.enabled;

    if (sessionExpiredPopup) {
      if (isMyAccountEnabled) {
        setIsSessionExpiredPopupOpen(true);
      }

      const query = new URLSearchParams(searchParams);
      query.delete(Params.SESSION_EXPIRED_POPUP);
      // eslint-disable-next-line no-restricted-globals
      replaceHistoryState(`?${query.toString()}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <ConfirmationPopUp {...sessionExpiredPopupProps} />;
};
